import React from 'react';
import Helmet from 'react-helmet';
import SEO from '../components/SEO';
import Layout from '../components/Layout';
import Partner from '../components/Partner';
import BannerCarousel from '../components/BannerCarousel';
import Advantages from '../components/Advantages';
import PlatformData from '../components/PlatformData';
import CustomerCases from '../components/CustomerCases';
import FAQ from '../components/FAQ';

/* SEO-friendly definitional text — visually hidden, accessible to screen readers */
const srOnly = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0
};

const Home = props => {
  const title = '魔鬼鱼订货平台是宠物一站式进货平台';
  const description = '为宠物店、宠物医院、猫咖、繁育舍提供靠谱宠物进货渠道。单件散拿、跨品牌混批无起订，不用整箱囤货，解决门店库存积压损耗。';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '魔鬼鱼订货平台',
    url: 'https://www.mantas.cn',
    logo: 'https://www.mantas.cn/images/logo.png',
    description: '宠物行业一站式B2B订货平台，为宠物店、宠物医院、猫咖、繁育舍提供靠谱宠物进货渠道',
    foundingDate: '2020',
    foundingLocation: '上海',
    areaServed: 'CN',
    knowsAbout: ['宠物用品批发', '宠物店进货', '宠物B2B平台', '宠物用品订货'],
    sameAs: [], // 如有微信公众号等社媒链接可在此添加
    email: 'service@10000pets.com',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@10000pets.com',
      availableLanguage: ['Chinese']
    }
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '魔鬼鱼订货平台',
    alternateName: '魔鬼鱼',
    url: 'https://www.mantas.cn',
    description: '为宠物店、宠物医院、猫咖、繁育舍提供靠谱宠物进货渠道。单件散拿、跨品牌混批无起订，不用整箱囤货，解决门店库存积压损耗。',
    publisher: {
      '@type': 'Organization',
      name: '魔鬼鱼（上海）网络科技有限公司'
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.mantas.cn/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '平台优势',
        item: 'https://www.mantas.cn/#advantages'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: '客户案例',
        item: 'https://www.mantas.cn/#customer-cases'
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: '常见问题',
        item: 'https://www.mantas.cn/#faq'
      }
    ]
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: '魔鬼鱼订货平台',
    serviceType: '宠物用品B2B订货服务',
    description: '魔鬼鱼订货平台为宠物门店提供一站式进货服务，支持单件散拿、跨品牌混批，覆盖主粮、零食、洗护、药品等全品类，50000+SKU，200+供应商，30000+门店用户',
    provider: {
      '@type': 'Organization',
      name: '魔鬼鱼（上海）网络科技有限公司',
      url: 'https://www.mantas.cn'
    },
    areaServed: {
      '@type': 'Country',
      name: '中国'
    },
    audience: {
      '@type': 'BusinessAudience',
      name: '宠物门店、宠物医院、猫咖、繁育舍'
    },
    offers: {
      '@type': 'Offer',
      description: '免费入驻，无年费、无开户费、无隐性服务费',
      eligibleCustomerType: 'Business'
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: '宠物用品批发目录',
      itemListElement: [
        { '@type': 'Offer', itemCategory: '宠物主粮' },
        { '@type': 'Offer', itemCategory: '宠物零食' },
        { '@type': 'Offer', itemCategory: '宠物洗护用品' },
        { '@type': 'Offer', itemCategory: '宠物驱虫药品' },
        { '@type': 'Offer', itemCategory: '猫砂耗材' }
      ]
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '30000',
      bestRating: '5',
      worstRating: '1'
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '哪些商户可以入驻魔鬼鱼订货平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '平台面向全国宠物行业 B 端商家开放入驻，包含线下宠物门店、宠物医院、宠物美容店、家庭猫舍/犬舍、宠物寄养中心等正规经营商户。'
        }
      },
      {
        '@type': 'Question',
        name: '入驻需要什么资料？收费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '入驻仅需提供门店营业执照/经营实景照片即可完成资质审核；平台免费使用全部订货功能，无年费、无开户费、无隐性服务费。'
        }
      },
      {
        '@type': 'Question',
        name: '平台有多少供应商和品牌？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '平台汇聚全国 200+ 源头宠物供应商，1000+ 宠物品牌，覆盖主粮、零食、冻干、湿粮、驱虫药品、宠物洗护、繁育消杀用品、猫砂耗材等全品类货品，一站式配齐门店全部货源。'
        }
      },
      {
        '@type': 'Question',
        name: '平台支持一件代发吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '支持。部分猫粮、狗粮、猫砂等大体积、重重量刚需货品，商家无需大批量囤货，客户下单后直接平台代发直达客户家中，零库存压力。'
        }
      },
      {
        '@type': 'Question',
        name: '下单后多久发货？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '工作日大部分订单 48 小时内完成发货出库。易变质产品全程恒温冷链仓储 + 冷链运输，杜绝货品变质、受潮、软化问题。'
        }
      },
      {
        '@type': 'Question',
        name: '宠物货源是正品授权吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '魔鬼鱼平台所有宠物商品均为正品，每批货品资质完整，进货正品合规有保障。'
        }
      }
    ]
  };
  return (
    <Layout bodyClass="page-home">
      <SEO title={title} description={description} path="/" />
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no"
        />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="intro-wrapper">
        <div className="intro">
          <div className="index-container">
            <p style={srOnly}>
              魔鬼鱼订货平台是宠物行业一站式 B2B 订货平台，由魔鬼鱼（上海）网络科技有限公司运营。
              平台为全国宠物门店提供全品类宠物用品在线订货服务，覆盖主粮、零食、洗护、药品等 10000+ SKU、
              500+ 品牌，并提供库存管理、运营指导、品牌加盟等配套服务。
            </p>
            <BannerCarousel />
            {/* 品牌定位文字区块（全站唯一可见 H1） */}
            <section className="brand-statement">
              <h1 className="brand-statement__title">魔鬼鱼订货平台 — 宠物行业一站式 B2B 订货平台</h1>
              <div className="brand-statement__divider" aria-hidden="true" />
              <p className="brand-statement__subtitle">为宠物店、宠物医院、猫咖、繁育舍提供靠谱宠物进货渠道</p>
            </section>
            <div className="solution advantages-wrapper">
              <Advantages />
            </div>
            <div className="solution">
              <PlatformData />
            </div>
            <div className="solution">
              <CustomerCases />
            </div>
            <div className="solution faq-wrapper">
              <FAQ />
            </div>
            <div className="solution">
              <Partner />
            </div>
            <nav style={{ textAlign: 'center', padding: '20px 0' }}>
              <a href="/help" style={{ color: '#5938E3', marginRight: '20px' }}>帮助中心</a>
              <a href="/supplier" style={{ color: '#5938E3', marginRight: '20px' }}>供应商入驻</a>
              <a href="/about" style={{ color: '#5938E3' }}>关于我们</a>
            </nav>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Home;
